import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { LifeSphere } from '../types';
import { BriefcaseIcon, HeartIcon, SunIcon, PaletteIcon, CheckCircleIcon } from './icons';

interface LifeSphereSelectorProps {
    activeSphere?: LifeSphere;
    onSelect: (sphere: LifeSphere) => void;
}

const LifeSphereSelector: React.FC<LifeSphereSelectorProps> = ({ activeSphere, onSelect }) => {
    const { t } = useTranslation();

    const spheres = [
        {
            id: LifeSphere.EXECUTIVE_ASCENDANCE,
            icon: BriefcaseIcon,
            titleKey: 'lifeSphere_executive_title',
            descKey: 'lifeSphere_executive_desc',
            defaultTitle: 'Executive Ascendance',
            defaultDesc: 'Elevated professional presence and power dressing.',
            color: 'blue'
        },
        {
            id: LifeSphere.ROMANTIC_RENAISSANCE,
            icon: HeartIcon,
            titleKey: 'lifeSphere_romantic_title',
            descKey: 'lifeSphere_romantic_desc',
            defaultTitle: 'Romantic Renaissance',
            defaultDesc: 'Alluring, confident styling for your love life.',
            color: 'rose'
        },
        {
            id: LifeSphere.EFFORTLESS_MATRIARCH,
            icon: SunIcon,
            titleKey: 'lifeSphere_matriarch_title',
            descKey: 'lifeSphere_matriarch_desc',
            defaultTitle: 'Effortless Matriarch',
            defaultDesc: 'Chic, functional elegance for family life.',
            color: 'amber'
        },
        {
            id: LifeSphere.CREATIVE_FLOURISHING,
            icon: PaletteIcon,
            titleKey: 'lifeSphere_creative_title',
            descKey: 'lifeSphere_creative_desc',
            defaultTitle: 'Creative Flourishing',
            defaultDesc: 'Bold silhouettes reflecting artistic growth.',
            color: 'purple'
        }
    ];

    return (
        <div className="w-full">
            <div className="mb-6 flex justify-between items-end">
                <div>
                    <h3 className="text-xl font-serif font-bold text-gray-900 dark:text-white">
                        {t('lifeSphere_selection_title') || 'Your Current Focus'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('lifeSphere_selection_desc') || 'Select your active Life Sphere to tailor recommendations.'}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {spheres.map((sphere) => {
                    const Icon = sphere.icon;
                    const isActive = activeSphere === sphere.id;

                    // Dynamic colors based on isActive and sphere color theme
                    const colorClasses: Record<string, string> = {
                        blue: isActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500' : 'border-gray-200 dark:border-gray-700 hover:border-blue-300',
                        rose: isActive ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 ring-1 ring-rose-500' : 'border-gray-200 dark:border-gray-700 hover:border-rose-300',
                        amber: isActive ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-500' : 'border-gray-200 dark:border-gray-700 hover:border-amber-300',
                        purple: isActive ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 ring-1 ring-purple-500' : 'border-gray-200 dark:border-gray-700 hover:border-purple-300',
                    };

                    const iconColorClasses: Record<string, string> = {
                        blue: isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400',
                        rose: isActive ? 'text-rose-600 dark:text-rose-400' : 'text-gray-400',
                        amber: isActive ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400',
                        purple: isActive ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400',
                    };

                    return (
                        <button
                            key={sphere.id}
                            onClick={() => onSelect(sphere.id)}
                            className={`relative text-left p-6 rounded-2xl border-2 transition-all duration-300 group ${colorClasses[sphere.color]}`}
                        >
                            {isActive && (
                                <motion.div
                                    layoutId="activeSphereIndicator"
                                    className="absolute -top-3 -right-3 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white shadow-lg z-10"
                                >
                                    <CheckCircleIcon className="w-5 h-5" />
                                </motion.div>
                            )}

                            <div className={`w-12 h-12 rounded-xl bg-white dark:bg-gray-800 shadow-sm flex items-center justify-center mb-4 transition-colors ${iconColorClasses[sphere.color]}`}>
                                <Icon className="w-6 h-6" />
                            </div>

                            <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                                {(t(sphere.titleKey as any) as string) !== sphere.titleKey ? t(sphere.titleKey as any) : sphere.defaultTitle}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                                {(t(sphere.descKey as any) as string) !== sphere.descKey ? t(sphere.descKey as any) : sphere.defaultDesc}
                            </p>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default LifeSphereSelector;
